import { useLocation } from 'react-router-dom';
import { useBootup } from '@/hooks/useBootup';
import { LoadingPage } from '@/components/screens/LoadingPage';
import { ResultsPending } from '@/components/shared/ResultsPending';
import { HomePage } from '@screens/HomePage/HomePage';
import { ErrorLoadingPage } from '@screens/ErrorHandling/ErrorLoadingPage';
import { getLottieFilesFromCdn } from '@/utils/nutrition/getImageUrl';
import { CancerDetectPage } from '@screens/CancerDetectPage';
import { NoActiveKitsPage } from '@screens/ErrorHandling/NoActiveKitsPage';

export interface BootupGuardProps {
  children?: React.ReactNode;
}

export const BootupGuard = ({ children }: BootupGuardProps) => {
  const { isLoading, isError, bootupState } = useBootup();
  const { kitDetailsData, CDkitDetailsData } = bootupState || {};
  const hideSideBarOptionsForCancerDetectUsers = CDkitDetailsData && !kitDetailsData;
  const isNoKitDetailsAvailable = (obj) => !obj || Object.keys(obj).length === 0;

  const location = useLocation();
  const locationPathName = location?.pathname;
  const nutritionLottieAnimate = getLottieFilesFromCdn(
    'coming_soon_light.json',
  );
  const healthLottieAnimate = getLottieFilesFromCdn('coming_soon_dark.json');

  if (isLoading) {
    return <LoadingPage />;
  }

  if (isError) {
    return <ErrorLoadingPage errorType='logout' />;
  }

  // Check if both kit type values are null and show the no kit page.
  if (isNoKitDetailsAvailable(kitDetailsData) && isNoKitDetailsAvailable(CDkitDetailsData)) {
    return <NoActiveKitsPage type='nokitregister' />;
  }

  if (
    !bootupState?.recommendationExists &&
    locationPathName?.includes('/nutrition')
  ) {
    return (
      <ResultsPending
        title="When your nutrition recommendations are ready, you'll find them here."
        videoSrc={nutritionLottieAnimate}
      />
    );
  }

  if (
    !bootupState?.recommendationExists &&
    locationPathName?.includes('/health')
  ) {
    return (
      <ResultsPending
        title="When your health recommendations are ready, you'll find them here."
        videoSrc={healthLottieAnimate}
        dark
        className="dark"
      />
    );
  }

  if (!bootupState?.recommendationExists && locationPathName === '/' && !hideSideBarOptionsForCancerDetectUsers) {
    return <HomePage />;
  }

  if (locationPathName === '/' && hideSideBarOptionsForCancerDetectUsers) {
    return <CancerDetectPage />;
  }

  return children;
};
